import {
  Dialog,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import React from "react";

import { deleteCustomer as deleteCustomerAPI } from "api/customers";
import { customersKeys } from "api/customers/queries";
import { CustomersType } from "api/customers/types";
import UltimateButton from "components/UltimateButton";

interface DeleteCustomerModalProps {
  isOpen: boolean;
  handleClose: () => void;
  customer: CustomersType | undefined;
}

const DeleteCustomerModal = ({
  isOpen,
  handleClose,
  customer,
}: DeleteCustomerModalProps) => {
  const theme = useTheme();
  const queryClient = useQueryClient();

  const { mutateAsync: deleteCustomer, status } = useMutation(
    async (customerId: string) => {
      const res = await deleteCustomerAPI(customerId);
      await queryClient.invalidateQueries(customersKeys.list());
      return res.data;
    },
  );

  const handleDelete = async () => {
    if (!customer?._id) return;
    await deleteCustomer(customer._id, {
      onSuccess: () => handleClose(),
    });
  };

  const displayName =
    customer?.firstName || customer?.lastName
      ? `${customer?.firstName ?? ""} ${customer?.lastName ?? ""}`.trim()
      : "this customer";

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 3,
          py: 2,
          pb: 3,
        },
      }}
    >
      <DialogTitle>
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="center"
          width="100%"
          spacing={0.5}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            p={1.5}
            sx={{
              bgcolor: (t) =>
                (t.palette as any).brandDanger?.secondary ?? "#FEE9E8",
              borderRadius: "50%",
            }}
          >
            <Trash2
              size={32}
              color={(theme.palette as any).brandDanger?.primary ?? "#EC1F3D"}
            />
          </Stack>
          <Typography variant="headingMD" fontWeight={600}>
            Delete customer
          </Typography>
          <Typography
            variant="textSM"
            textAlign="center"
            sx={{
              color: (t) =>
                (t.palette as any).brandText?.secondary ?? "#A2A8B6",
            }}
          >
            Are you sure you want to delete{" "}
            <span
              style={{
                color: (theme.palette as any).brandText?.primary ?? "#111726",
              }}
            >
              {displayName}
            </span>
            ? This removes records from Lendzee and HubSpot. This action cannot
            be undone.
          </Typography>
        </Stack>
      </DialogTitle>

      <Stack width="100%" alignItems="center" justifyContent="center">
        <UltimateButton
          text="Delete"
          color="brandDanger"
          Icon={
            Trash2 as React.ComponentType<{
              size?: number;
              className?: string;
              style?: React.CSSProperties;
            }>
          }
          onClick={handleDelete}
          loading={status === "loading"}
          disabled={!customer?._id}
          sx={{ py: 1 }}
        />
      </Stack>
    </Dialog>
  );
};

export default DeleteCustomerModal;
